import { Button, Input } from "@blurb/codex-react";
import { Group, MantineProvider, Stack } from "@mantine/core";
import { GraphQL } from "@rpi/openapi-api";
import { Flex, IconV3, RpiLink, RpiText } from "@rpi/openapi-core";
import React from "react";
import { useOutlet } from "react-router-dom";
import { AppPage } from "../../components/AppPage";
import { RpiPagination } from "../../components/RpiPagination";
import { RpiSearchFilter } from "../../components/search-filter/RpiSearchFilter";
import { useCustomerProductFilters } from "../../configuration/filters/customerProduct.filter";
import { useAuthUser } from "../../hooks/useAuthUser";
import { useRpiPagination } from "../../hooks/usePagination";
import { rpaTheme } from "../../theme/rpaTheme";
import { SearchGeneral, SearchInputs, SearchSort } from "../../utils/graphql.util";
import { PublicEmail } from "../../utils/link.util";
import { MailTo } from "../../utils/mail.util";
import { isNumber } from "../../utils/type.util";
import { CustomerProductCreateView, path as createPath } from "./CustomerProductCreateView";
import { PricingCalculator } from "../index/PricingCalculator";
import { CustomerProductsTableV1 } from "./CustomerProductsTableV1";
import { CustomerProductsTableFooter, CustomerProductsTableV2 } from "./CustomerProductsTableV2";
import { useCustomerProducts } from "../../hooks/useCustomerProducts";
import { PaginationV2 } from "../../components/PaginationV2";
import { useForm } from "@mantine/form";
import { CustomerProductFilter, path as filterPath } from "./CustomerProductFilter";
import { featureFlags } from "../../featureFlags";
import { CustomerProductViewV2 } from "./CustomerProductView";
import { useDrawerRoute } from "../../hooks/useDrawerRoute";
import { useNavigateRoute } from "../../hooks/useNavigateRoute";

export const CustomerProductsView: React.FC = () => {
  const user = useAuthUser();
  const navigateRoute = useNavigateRoute();
  const outlet = useOutlet();

  const createDrawerRoute = useDrawerRoute(createPath);
  const detailsDrawerRoute = useDrawerRoute("", /customer-products\/\d+\/?$/);
  const filterDrawerRoute = useDrawerRoute(filterPath);
  const editDrawerRoute = useDrawerRoute("", /\d+\/edit\/?$/);
  const [isPriceCalculatorOpen, setIsPriceCalculatorOpen] = React.useState(false);

  const [sortInput, setSortInput] = React.useState<SearchSort<GraphQL.CustomerProductsQueryVariables>>({
    by: GraphQL.CustomerProductSearchField.Id,
    sortDirection: GraphQL.SortDirection.Desc,
  });
  const [searchInputs, setSearchInputs] = React.useState<SearchInputs<GraphQL.CustomerProductsQueryVariables>>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.CustomerProductsQueryVariables>>();

  const pagination = useRpiPagination({ perPage: 20 });

  const customerProductFilters = useCustomerProductFilters();

  const { data, error, isLoading, refetch } = useCustomerProducts(user.is.SUPER_ADMIN, pagination, {
    inputs: searchInputs,
    sort: sortInput,
    generalInput: searchGeneral,
  });

  const customerProducts = data?.customerProducts.data;

  const { data: customerData } = GraphQL.useCustomerQuery({ customerPublicId: user.id! });
  const customer = React.useMemo(() => customerData?.customer, [customerData]);

  React.useEffect(() => {
    if (outlet === null) {
      refetch();
    }
  }, [outlet]);

  const maximumOrdersMessage = React.useMemo(() => {
    if (!customer) return;

    const { maxOrders } = customer?.settings?.ordersLimit || {};

    return (
      <>
        {!isNumber(maxOrders)
          ? ""
          : maxOrders > 0
          ? `You can place up to ${maxOrders} orders in a day. `
          : "You can place unlimited amount of orders in a day. "}
        {!isNumber(maxOrders)
          ? "If you want to set order limit"
          : maxOrders > 0
          ? "If you want to increase this number"
          : "If you want to limit this number"}
        ,
        <br />
        please reach out to RPI CS team{" "}
        <RpiLink
          variant="inherit"
          href={MailTo({ email: PublicEmail.SUPPORT, subject: `Increase order limit for "${customer.publicId}"` })}
          children={PublicEmail.SUPPORT}
        />
        .
      </>
    );
  }, [customer]);

  const [productId, setProductId] = React.useState<number | undefined>(undefined);

  const form = useForm<{ searchValue: string }>({
    initialValues: {
      searchValue: "",
    },
  });

  const handleSubmit = (input: { searchValue: string }) => {
    setSearchGeneral(input.searchValue);
  };

  return (
    <AppPage path={["Products"]} onReload={refetch}>
      <MantineProvider theme={rpaTheme}>
        <CustomerProductCreateView isOpened={createDrawerRoute.isOpen} onClose={createDrawerRoute.close} />
        <PricingCalculator
          isOpened={isPriceCalculatorOpen}
          onClose={() => setIsPriceCalculatorOpen(false)}
          productId={productId}
          customerProducts={customerProducts}
        />
      </MantineProvider>
      {featureFlags.newTableView && user.is.SUPER_ADMIN && (
        <MantineProvider theme={rpaTheme}>
          <CustomerProductFilter
            isOpened={filterDrawerRoute.isOpen}
            onClose={filterDrawerRoute.close}
            filters={searchInputs}
            onApply={setSearchInputs}
          />
        </MantineProvider>
      )}
      {featureFlags.newTableView ? (
        <MantineProvider theme={rpaTheme}>
          <CustomerProductViewV2 isOpened={detailsDrawerRoute.isOpen} onClose={detailsDrawerRoute.close} />
        </MantineProvider>
      ) : (
        detailsDrawerRoute.isOpen && outlet
      )}
      {editDrawerRoute.isOpen && outlet}
      <AppPage.Section>
        <Group position={"apart"}>
          {featureFlags.newTableView ? (
            <MantineProvider theme={rpaTheme}>
              <Group spacing="sm">
                <form onSubmit={form.onSubmit(handleSubmit)}>
                  <Group spacing="sm">
                    <Input
                      disabled={isLoading}
                      style={{ width: 502 }}
                      placeholder="Search by Product Name or SKU"
                      {...form.getInputProps("searchValue")}
                    />
                    <Button
                      variant="outlined"
                      intent="neutral"
                      type="submit"
                      data-testid="search-button"
                      disabled={isLoading}
                    >
                      Search
                    </Button>
                  </Group>
                </form>
                {user.is.SUPER_ADMIN && (
                  <Button variant="outlined" intent="neutral" onClick={filterDrawerRoute.open} disabled={isLoading}>
                    <IconV3 icon="tune" color="inherit" size={20} />
                    Filters
                  </Button>
                )}
              </Group>
            </MantineProvider>
          ) : (
            <RpiSearchFilter
              placeholder="Search by Product Name or SKU"
              filters={customerProductFilters}
              onChange={setSearchInputs}
              onSearch={setSearchGeneral}
            />
          )}

          <MantineProvider theme={rpaTheme}>
            <Group spacing="sm">
              {user.is.SUPER_ADMIN && (
                <Button variant="filled" intent="primary" onClick={() => navigateRoute(["open-api-products"])}>
                  <Group spacing="xs">
                    <IconV3 icon="format_list_bulleted" color="inherit" size={20} /> Catalog
                  </Group>
                </Button>
              )}
              <Button
                variant="filled"
                intent="primary"
                onClick={createDrawerRoute.open}
                data-testid="addNewProductButton"
              >
                <Group spacing={"xs"}>
                  <IconV3 icon="add" color="inherit" /> Add New Product
                </Group>
              </Button>
            </Group>
          </MantineProvider>
        </Group>
        {featureFlags.newTableView ? (
          <MantineProvider theme={rpaTheme}>
            <CustomerProductsTableV2
              customerProducts={customerProducts}
              isLoading={isLoading}
              error={error}
              setIsPriceCalculatorOpen={setIsPriceCalculatorOpen}
              setProductId={setProductId}
            >
              <CustomerProductsTableFooter>
                <PaginationV2 isLoading={isLoading} pagination={pagination} />
              </CustomerProductsTableFooter>
            </CustomerProductsTableV2>
          </MantineProvider>
        ) : (
          <Stack>
            <CustomerProductsTableV1
              customerProducts={customerProducts}
              isLoading={isLoading}
              error={error}
              searchInputs={searchInputs}
              searchGeneral={searchGeneral}
              setSortInput={setSortInput}
              pagination={pagination}
              setIsPriceCalculatorOpen={() => setIsPriceCalculatorOpen(true)}
              setProductId={setProductId}
            />
            <Flex.Row fullWidth justify="space-between">
              <RpiText
                type="p3"
                weight="regular"
                color={(theme) => theme.colors.brand[5]}
                children={maximumOrdersMessage}
              />
              <RpiPagination centered={false} pagination={pagination} />
            </Flex.Row>
          </Stack>
        )}
      </AppPage.Section>
    </AppPage>
  );
};
